import {useEffect, useMemo, useState} from "react";
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "@headlessui/react";
import type {Role, User} from "@/hooks/useUsers";
import {useSaveUser} from "@/hooks/useUsers";
import {notify} from "@/components/Notifications.tsx";
import {getErrMsg} from "@/lib/errors.ts";

type Props = {
    open: boolean;
    initial?: Partial<User> | null;
    onClose: () => void;
};

export default function UserUpsertDialog({open, initial, onClose}: Props) {
    const isEdit = Boolean(initial?.id);

    const [name, setName] = useState(initial?.name ?? "");
    const [email, setEmail] = useState(initial?.email ?? "");
    const [roles, setRoles] = useState<Role[]>(initial?.roles ?? []);
    const [enabled, setEnabled] = useState<boolean>(initial?.enabled ?? true);
    const [avatarUrl, setAvatarUrl] = useState(initial?.avatarUrl ?? "");
    const [password, setPassword] = useState("");
    const [pwVisible, setPwVisible] = useState(false);
    const [touched, setTouched] = useState(false);

    const {mutateAsync: save, isPending} = useSaveUser();

    useEffect(() => {
        if (open) {
            setName(initial?.name ?? "");
            setEmail(initial?.email ?? "");
            setRoles(initial?.roles ?? []);
            setEnabled(initial?.enabled ?? true);
            setAvatarUrl(initial?.avatarUrl ?? "");
            setPassword("");
            setPwVisible(false);
            setTouched(false);
        }
    }, [open, initial]);

    const errors = useMemo(() => {
        const e: Record<string, string> = {};
        if (!name.trim()) e.name = "Name is required.";
        if (!email.trim()) e.email = "Email is required.";
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = "Invalid email.";
        if (!isEdit && !password.trim()) e.password = "Password is required.";
        if (!isEdit && password && password.length < 8) e.password = "Password must be ≥ 8 characters.";

        const allowed: Role[] = ["ADMIN", "EDITOR"];
        if (!roles.length || !roles.every(r => allowed.includes(r)))
            e.roles = "Select at least one valid role (ADMIN, EDITOR).";

        if (avatarUrl && avatarUrl.length > 2048) e.avatarUrl = "Avatar URL too long.";
        return e;
    }, [name, email, password, roles, isEdit]);


    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (Object.keys(errors).length) return;

        try {
            await save({
                id: initial?.id,
                name,
                email,
                roles,
                enabled,
                avatarUrl,
                password: isEdit ? undefined : password,
            });
            onClose();
        } catch (e) {
            notify.error(getErrMsg(e, `Failed to ${isEdit ? "update" : "create"} user. Please try again.`));
        }
    };

    return (
        <Dialog open={open} onClose={onClose} className="relative z-10">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-800/75 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in"
            />
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative w-full max-w-md sm:max-w-lg md:max-w-xl transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in sm:my-8 sm:p-6 sm:data-[closed]:translate-y-0 sm:data-[closed]:scale-95"
                    >
                        <DialogTitle as="h3" className="text-lg font-semibold text-blue-900">
                            {isEdit ? "Edit User" : "New User"}
                        </DialogTitle>

                        <form onSubmit={onSubmit} noValidate>
                            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="user-name"
                                           className="block text-sm font-medium text-blue-900">Name</label>
                                    <input
                                        id="user-name"
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        aria-invalid={touched && !!errors.name}
                                        aria-describedby={errors.name ? "user-name-error" : undefined}
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                    />
                                    {touched && errors.name &&
                                        <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                                </div>

                                <div>
                                    <label htmlFor="user-email"
                                           className="block text-sm font-medium text-blue-900">Email</label>
                                    <input
                                        id="user-email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        disabled={isEdit}
                                        aria-invalid={touched && !!errors.email}
                                        aria-describedby={errors.email ? "user-email-error" : undefined}
                                        className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                    />
                                    {touched && errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
                                </div>

                                {!isEdit && (
                                    <div className="sm:col-span-2">
                                        <label htmlFor="user-password"
                                               className="block text-sm font-medium text-blue-900">
                                            Password
                                        </label>
                                        <div className="mt-2 flex gap-2">
                                            <input
                                                id="user-password"
                                                type={pwVisible ? "text" : "password"}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                                aria-invalid={touched && !!errors.password}
                                                aria-describedby={errors.password ? "user-password-error" : undefined}
                                                className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-blue-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-blue-800 sm:text-sm/6"
                                                placeholder="At least 8 characters"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setPwVisible((v) => !v)}
                                                className="rounded-md border px-3 text-sm"
                                            >
                                                {pwVisible ? "Hide" : "Show"}
                                            </button>
                                        </div>
                                        {touched && errors.password && (
                                            <p id="user-password-error" className="mt-1 text-xs text-red-600">
                                                {errors.password}
                                            </p>
                                        )}
                                    </div>
                                )}

                                <div className="sm:col-span-2">
                                    <fieldset>
                                        <legend className="block text-sm font-medium text-blue-900">Roles</legend>
                                        <p className="mt-1 text-xs text-gray-500">Select one or both.</p>
                                        <div className="mt-2 space-y-2">
                                            {(["ADMIN", "EDITOR"] as Role[]).map((r) => (
                                                <label key={r} className="flex items-center gap-2 text-sm">
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4"
                                                        checked={roles.includes(r)}
                                                        onChange={(e) => {
                                                            setRoles(prev =>
                                                                e.target.checked
                                                                    ? Array.from(new Set([...prev, r]))
                                                                    : prev.filter(x => x !== r)
                                                            );
                                                        }}
                                                    />
                                                    <span>{r}</span>
                                                </label>
                                            ))}
                                        </div>
                                        {touched && errors.roles && (
                                            <p className="mt-1 text-xs text-red-600">{errors.roles}</p>
                                        )}
                                    </fieldset>
                                </div>

                            </div>

                            <div className="mt-6 flex justify-end gap-x-3">
                                <button type="button" onClick={onClose}>Cancel</button>
                                <button type="submit"
                                        className="inline-flex items-center rounded-md bg-blue-900 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 disabled:opacity-60"
                                        disabled={isPending}>
                                    {isPending ? (isEdit ? "Saving…" : "Creating…") : isEdit ? "Save" : "Create"}
                                </button>
                            </div>
                        </form>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
